import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { shippingApi } from "./api";

function requestKey() {
  if (!window.crypto?.getRandomValues) {
    throw new Error("Secure request IDs are unavailable. Open the application in a supported browser over HTTPS.");
  }
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export default function SourceHealthPanel({ capabilities, orderId, onAccessLost, phase = "PREPARATION", onHealth, compact = false }) {
  const [health, setHealth] = useState(orderId ? null : capabilities.source_health);
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [receipt, setReceipt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [revision, setRevision] = useState(0);
  const key = useRef(null);
  const command = useRef(null);
  const mounted = useRef(true);
  const seconds = Math.max(30, Math.min(300, capabilities.source_health?.poll_seconds || 60));
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; command.current?.abort(); };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    let pending = false;
    async function load() {
      if (pending) return;
      pending = true;
      try {
        const value = await shippingApi.sourceHealth(orderId, controller.signal);
        if (!controller.signal.aborted) { setHealth(value); setLoadError(""); onHealth?.(value); }
      } catch (err) {
        if (controller.signal.aborted) return;
        setHealth(null);
        onHealth?.(null);
        if (err.status === 401 || err.status === 403) onAccessLost(err);
        else setLoadError(err.message || "Source health is unavailable.");
      } finally { pending = false; }
    }
    load();
    const timer = setInterval(load, seconds * 1000);
    return () => { clearInterval(timer); controller.abort(); };
  }, [orderId, revision, seconds, onAccessLost, onHealth]);

  async function request() {
    setBusy(true); setError("");
    try {
      key.current = key.current || requestKey(); // Retain across timeout/unknown outcome retries.
      command.current = new AbortController();
      const result = await shippingApi.requestSource(orderId, key.current, capabilities.csrf_token, command.current.signal, phase);
      if (!mounted.current) return;
      setReceipt(result); key.current = null; setRevision((value) => value + 1);
    } catch (err) {
      if (!mounted.current || err.name === "AbortError") return;
      if (err.status === 401 || err.status === 403) onAccessLost(err);
      else { setError(err.message || "Outcome unknown. Retry this request."); setRevision((value) => value + 1); }
    } finally { if (mounted.current) setBusy(false); }
  }

  const run = orderId ? health?.observation : health?.latest_run;
  const pending = run && ["QUEUED", "RUNNING"].includes(run.status);
  const allowed = health?.can_request && (orderId || capabilities.user.role === "admin");
  const ready = orderId ? health?.readiness === "CHECKED" : health?.status === "CURRENT";
  const title = orderId ? "eBay safety check" : "Order import health";
  const statusLine = health
    ? `${orderId ? (health.readiness || "CHECK_REQUIRED") : health.status}${health.reason ? ` · ${health.reason}` : ""}`
    : "Reading source health…";

  const details = <>
    {health && <>
      {health.can_request && <Typography variant="body2">eBay environment: {health.ebay_environment} · Last successful import: {health.last_successful_import || "None recorded"} · Lag: {health.lag_seconds == null ? "Unknown" : `${health.lag_seconds}s`}</Typography>}
      {run && <Typography variant="body2">Run {run.id}: {run.status}{run.error_code ? ` · ${run.error_code}` : ""}</Typography>}
      {orderId && <Typography variant="body2">Source readiness: {health.readiness || "CHECK_REQUIRED"}. This is not authorisation to pack or dispatch.</Typography>}
      {orderId && health.safety?.issues?.map((issue) => <Typography variant="caption" component="div" key={issue}>{issue}</Typography>)}
    </>}
    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    {loadError && <Alert severity="error" sx={{ mt: 1 }}>{loadError}</Alert>}
    {receipt && <Typography variant="caption" component="div" sx={{ mt: 1 }}>Request recorded as run {receipt.run_id}. A separately supervised worker processes it.</Typography>}
    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
      {allowed && <Button variant="outlined" disabled={busy || Boolean(pending)} onClick={request}>
        {busy ? "Recording request…" : key.current ? "Retry source request" : orderId ? phase === "FINALISATION" ? "Request final eBay safety check" : "Request eBay safety check" : "Request eBay order sync"}
      </Button>}
      <Button size="small" onClick={() => setRevision((value) => value + 1)}>Refresh status</Button>
    </Stack>
    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.75 }}>
      Status polling and local Refresh do not call eBay. Requested sync uses the existing importer and its existing local profit/consignment reconciliation.
    </Typography>
  </>;

  if (compact) {
    return <Box sx={{ mb: orderId ? 0 : 0 }}>
      <Alert severity={ready ? "info" : "warning"} variant="outlined" sx={{ py: 0.25 }}>
        <Typography variant="subtitle2">{title}</Typography>
        <Typography variant="body2">{statusLine}</Typography>
        {(error || loadError) && <Typography variant="caption" color="error.main" component="div">Source-health detail requires attention.</Typography>}
      </Alert>
      <Box component="details" sx={{ mt: 0.75, px: 0.5 }}>
        <Typography component="summary" variant="caption" sx={{ cursor: "pointer", color: "text.secondary", userSelect: "none" }}>
          Safety-check details and controls
        </Typography>
        <Box sx={{ pt: 1 }}>{details}</Box>
      </Box>
    </Box>;
  }

  return <Box sx={{ mb: 2 }}>
    <Alert severity={ready ? "info" : "warning"}>
      <Typography variant="subtitle2">{orderId ? "Per-order eBay safety check" : "Order import health"}</Typography>
      <Typography variant="body2">{statusLine}</Typography>
      {health ? <>
        {health.can_request && <Typography variant="body2">eBay environment: {health.ebay_environment} · Last successful import: {health.last_successful_import || "None recorded"} · Lag: {health.lag_seconds == null ? "Unknown" : `${health.lag_seconds}s`}</Typography>}
        {run && <Typography variant="body2">Run {run.id}: {run.status}{run.error_code ? ` · ${run.error_code}` : ""}</Typography>}
        {orderId && <Typography variant="body2">Source readiness: {health.readiness || "CHECK_REQUIRED"}. This is not authorisation to pack or dispatch.</Typography>}
        {orderId && health.safety?.issues?.map((issue) => <Typography variant="caption" component="div" key={issue}>{issue}</Typography>)}
      </> : <Typography variant="body2">Reading source health…</Typography>}
    </Alert>
    {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
    {loadError && <Alert severity="error" sx={{ mt: 1 }}>{loadError}</Alert>}
    {receipt && <Typography variant="caption" component="div">Request recorded as run {receipt.run_id}. A separately supervised worker processes it.</Typography>}
    <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mt: 1 }}>
      {allowed && <Button variant="outlined" disabled={busy || Boolean(pending)} onClick={request}>
        {busy ? "Recording request…" : key.current ? "Retry source request" : orderId ? phase === "FINALISATION" ? "Request final eBay safety check" : "Request eBay safety check" : "Request eBay order sync"}
      </Button>}
      <Button size="small" onClick={() => setRevision((value) => value + 1)}>Refresh status</Button>
    </Stack>
    <Typography variant="caption">Status polling and local Refresh do not call eBay. Requested sync uses the existing importer and its existing local profit/consignment reconciliation.</Typography>
  </Box>;
}
